import * as React from "react";
import { useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import {
  OutlinedInput,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import BootstrapDialog from "../../../utils/bootstrap-dialog-style/BootstrapDialog";

export const CHANGE_PASSWORD_DETAILS = {
  CURRENT_PASSWORD: "currentPassword",
  NEW_PASSWORD: "newPassword",
  CONFIRM_PASSWORD: "confirmPassword",
};

const PASSWORD_STRENGTH_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

interface ChangePasswordFormProps {
  handleClose: () => void;
  handleOnSubmittedData: (formData: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => void;
}

const PasswordField = ({
  label,
  name,
  value,
  placeholder,
  error,
  helperText,
  onChange,
  onBlur,
}: {
  label: string;
  name: string;
  value: string;
  placeholder: string;
  error: boolean;
  helperText?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <div
        className="font-size-14px"
        style={{ color: "#727272", fontWeight: 500, marginBottom: "6px" }}
      >
        {label}
      </div>
      <OutlinedInput
        fullWidth
        size="small"
        name={name}
        placeholder={placeholder}
        error={error}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={show ? "hide the password" : "display the password"}
              onClick={() => setShow((s) => !s)}
              edge="end"
            >
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
      />
      {error && helperText && (
        <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
          {helperText}
        </div>
      )}
    </div>
  );
};

const ChangePasswordForm = ({
  handleClose,
  handleOnSubmittedData,
}: ChangePasswordFormProps) => {
  const validationSchema = Yup.object().shape({
    [CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD]: Yup.string().required(
      "Current password is required"
    ),
    [CHANGE_PASSWORD_DETAILS.NEW_PASSWORD]: Yup.string()
      .required("New password is required")
      .matches(
        PASSWORD_STRENGTH_REGEX,
        "Must be 8+ chars with uppercase, lowercase, digit and special character"
      )
      .notOneOf(
        [Yup.ref(CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD)],
        "New password must differ from current password"
      ),
    [CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD]: Yup.string()
      .required("Confirm password is required")
      .oneOf(
        [Yup.ref(CHANGE_PASSWORD_DETAILS.NEW_PASSWORD)],
        "Passwords do not match"
      ),
  });

  return (
    <Formik
      initialValues={{
        [CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD]: "",
        [CHANGE_PASSWORD_DETAILS.NEW_PASSWORD]: "",
        [CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD]: "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        try {
          handleOnSubmittedData({
            currentPassword: values[CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD],
            newPassword: values[CHANGE_PASSWORD_DETAILS.NEW_PASSWORD],
            confirmPassword: values[CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD],
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ errors, touched, values, handleChange, handleBlur, handleSubmit }) => (
        <Form
          onSubmit={handleSubmit}
          className="h-full flex flex-col justify-between rounded-lg"
        >
          <div className="w-full flex flex-col gap-4 rounded-lg">
            <PasswordField
              label="Current Password"
              name={CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD}
              placeholder="Enter current password"
              value={values[CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(
                touched[CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD] &&
                  errors[CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD]
              )}
              helperText={
                touched[CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD]
                  ? (errors[CHANGE_PASSWORD_DETAILS.CURRENT_PASSWORD] as string)
                  : undefined
              }
            />
            <PasswordField
              label="New Password"
              name={CHANGE_PASSWORD_DETAILS.NEW_PASSWORD}
              placeholder="Enter new password"
              value={values[CHANGE_PASSWORD_DETAILS.NEW_PASSWORD]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(
                touched[CHANGE_PASSWORD_DETAILS.NEW_PASSWORD] &&
                  errors[CHANGE_PASSWORD_DETAILS.NEW_PASSWORD]
              )}
              helperText={
                touched[CHANGE_PASSWORD_DETAILS.NEW_PASSWORD]
                  ? (errors[CHANGE_PASSWORD_DETAILS.NEW_PASSWORD] as string)
                  : undefined
              }
            />
            <PasswordField
              label="Confirm Password"
              name={CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD}
              placeholder="Re-enter new password"
              value={values[CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD]}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(
                touched[CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD] &&
                  errors[CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD]
              )}
              helperText={
                touched[CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD]
                  ? (errors[CHANGE_PASSWORD_DETAILS.CONFIRM_PASSWORD] as string)
                  : undefined
              }
            />
          </div>

          <div className="flex justify-end mt-6 gap-4">
            <Button
              label={TEXT_CONSTANTS?.CANCEL}
              variant="outlined"
              onClickCb={() => {
                if (handleClose) {
                  handleClose();
                }
              }}
              style={{
                width: "120px",
                height: "44px",
                padding: "12px 20px",
                fontSize: "16px",
                backgroundColor: "white",
                border: "none",
                color: "#393939",
              }}
            />
            <Button
              type="submit"
              label={TEXT_CONSTANTS?.SAVE}
              variant="contained"
              onClickCb={() => {}}
              style={{
                width: "120px",
                height: "44px",
                padding: "12px 20px",
                fontSize: "16px",
              }}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

interface ChangePasswordDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  handleChangePassword: (data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => void;
}

export default function ChangePasswordDialog({
  isOpen,
  handleClose,
  handleChangePassword,
}: ChangePasswordDialogProps) {
  return (
    <BootstrapDialog
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="change-password-dialog-title"
      open={isOpen}
    >
      <div
        className="flex justify-between items-center rounded-lg"
        style={{ margin: "14px 24px" }}
      >
        <Heading title="Change Password" />
        <div onClick={handleClose} className="cursor-pointer">
          <CloseIcon />
        </div>
      </div>

      <DialogContent dividers>
        <ChangePasswordForm
          handleClose={handleClose}
          handleOnSubmittedData={handleChangePassword}
        />
      </DialogContent>
    </BootstrapDialog>
  );
}
